import axios from "axios";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoaderSpiner from "../../Components/LoaderSpiner";
import AgentCallHistory from "../AgentCallHistory/AgentCallHistory";
import CallHistory from "../Client/CallHistory/CallHistory";
import styles from "./CallLogsCampaign.module.css";

const CallLogsCampaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [callHistoryToggle, setCallHistoryToggle] = useState(false);
  const [callType, setCallType] = useState("outbound");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");
  const navigate = useNavigate();

  useEffect(() => {
    console.log(
      "State Update - callHistoryToggle:",
      callHistoryToggle,
      "selectedCampaignId:",
      selectedCampaignId,
      "selectedAgentId:",
      selectedAgentId,
      "callType:",
      callType
    );
  }, [callHistoryToggle, selectedCampaignId, selectedAgentId, callType]);

  useEffect(() => {
    if (!token || !userRole || !userId) {
      console.log("Missing auth data, redirecting to login");
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        if (callType === "outbound") {
          let response;
          if (userRole === "vendor") {
            response = await axios.get(
              `https://prod.toxboxai.com/api/campaign/getallcampaign?_t=${Date.now()}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          } else {
            response = await axios.get(
              `https://prod.toxboxai.com/api/campaign/assigned-campaigns?_t=${Date.now()}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          }

          if (response.data.success) {
            const normalizedCampaigns = (
              response.data.data.campaigns || []
            ).map((campaign) => ({
              ...campaign,
              campaignname: campaign.campaignname || "N/A",
              type: campaign.type || "N/A",
              startDate: campaign.startDate || null,
              endDate: campaign.endDate || null,
              targetSegment: campaign.targetSegment || "N/A",
              leadGenerated: campaign.leadGenerated || 0,
              budget: campaign.budget || 0,
              spend: campaign.spend || 0,
              roi: campaign.roi || 0,
              initialCredit: campaign.creditBalance || 0,
              developerId: campaign.developerId || "N/A",
              projectId: campaign.projectId || {
                projectname: "N/A",
                _id: null,
              },
              createdBy: campaign.createdBy || { email: "N/A" },
              assignedParticipants: (campaign.assignedParticipants || []).map(
                (participant) => ({
                  _id: participant._id || participant,
                  name: participant.name || "Unknown",
                })
              ),
              sheetData: campaign.sheetData || [],
            }));
            setCampaigns(normalizedCampaigns);
            setAgents([]);
            setError("");
          } else {
            setError(response.data.error || "Failed to fetch campaigns");
          }
        } else {
          const response = await axios.get(
            `https://prod.toxboxai.com/api/agent-creation/inbound-by-creator/${userId}?_t=${Date.now()}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.data.agents) {
            setAgents(
              response.data.agents.filter((agent) => agent.type === "inbound")
            );
            setCampaigns([]);
            setError("");
          } else {
            setError("Failed to fetch inbound agents");
          }
        }
      } catch (err) {
        if (err.response?.status === 401) {
          console.log("Unauthorized, clearing auth data");
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          localStorage.removeItem("role");
          navigate("/login");
        } else {
          setError(`Failed to fetch data: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, userRole, userId, navigate, callType]);

  const handleCardClick = useCallback(
    (id) => {
      console.log(`${callType} clicked, ID:`, id);
      setSelectedCampaignId(id);
      setCallHistoryToggle(true);
    },
    [callType]
  );

  const handleAgentClick = useCallback(
    (id) => {
      console.log(`${callType} clicked, Agent ID:`, id);
      setSelectedAgentId(id);
      setCallHistoryToggle(true);
    },
    [callType]
  );

  const handleBack = useCallback(() => {
    console.log("handleBack triggered");
    setSelectedCampaignId(null);
    setSelectedAgentId(null);
    setCallHistoryToggle(false);
  }, []);

  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.campaignname.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredAgents = agents.filter((agent) =>
    agent.avatarName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={styles.container}>
      {callHistoryToggle ? (
        <>
          {callType === "outbound" ? (
            <CallHistory campaignId={selectedCampaignId} onBack={handleBack} />
          ) : (
            <AgentCallHistory agentId={selectedAgentId} onBack={handleBack} />
          )}
        </>
      ) : (
        <>
          <div className={styles.header}>
            <h2>
              {callType === "outbound"
                ? userRole === "vendor"
                  ? "All Campaigns"
                  : "Assigned Campaigns"
                : "Inbound Agents"}
            </h2>
            <div className={styles.controls}>
              <div className={styles.toggleBar}>
                <button
                  className={`${styles.toggleButton} ${callType === "outbound" ? styles.active : ""
                    }`}
                  onClick={() => setCallType("outbound")}
                >
                  Outbound
                </button>
                <button
                  className={`${styles.toggleButton} ${callType === "inbound" ? styles.active : ""
                    }`}
                  onClick={() => setCallType("inbound")}
                >
                  Inbound
                </button>
              </div>
              <input
                type="text"
                placeholder={
                  callType === "outbound"
                    ? "Search campaigns..."
                    : "Search agents..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {loading && <LoaderSpiner />}
          {error && <p className={styles.error}>{error}</p>}
          {!loading &&
            campaigns.length === 0 &&
            agents.length === 0 &&
            !error && (
              <p className={styles.noData}>
                {callType === "outbound"
                  ? "No campaigns found."
                  : "No inbound agents found."}
              </p>
            )}

          <div className={styles.cardContainer}>
            {callType === "outbound"
              ? filteredCampaigns.map((campaign) => (
                <motion.div
                  key={campaign._id}
                  className={styles.card}
                  onClick={() => handleCardClick(campaign._id)}
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className={styles.cardHeader}>
                    <h3>{campaign.campaignname}</h3>
                  </div>
                  <div className={styles.cardBody}>
                    <p>
                      <strong>Start:</strong>{" "}
                      {campaign.startDate
                        ? new Date(campaign.startDate).toLocaleDateString(
                          "en-US",
                          {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          }
                        )
                        : "N/A"}
                    </p>
                    <p>
                      <strong>End:</strong>{" "}
                      {campaign.endDate
                        ? new Date(campaign.endDate).toLocaleDateString(
                          "en-US",
                          {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          }
                        )
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Credit:</strong> {campaign.initialCredit}
                    </p>
                  </div>
                </motion.div>
              ))
              : filteredAgents.map((agent) => (
                <motion.div
                  key={agent._id}
                  className={styles.card}
                  onClick={() => handleAgentClick(agent.agentId || agent._id)} // Fallback to _id if agentId is undefined
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className={styles.cardHeader}>
                    <h3>{agent.avatarName}</h3>
                  </div>
                  <div className={styles.cardBody}>
                    <p>
                      <strong>Language:</strong> {agent.language}
                    </p>
                    <p>
                      <strong>Gender:</strong> {agent.gender}
                    </p>
                    <p>
                      <strong>Voice:</strong> {agent.voice.displayName}
                    </p>
                  </div>
                </motion.div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CallLogsCampaign;
